import {
  HistoryMessageItem,
  isTextMessage,
  MessageCallReceived,
  MessageCallRecordCompleted,
  MessageIncomingEvent,
  MessageOutgoingEvent,
  useIM,
} from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed, ref, watch, type Ref } from 'vue'

import { chatModel } from '@/entities/messages'
import { useChatFilesMarkersStore } from '@/entities/room'

import sort from '@/shared/utils/sort'
import { pServedIsRoom } from '@/shared/utils/uri'

import { GALLERY_HISTORY_BATCH_LIMIT, GALLERY_HISTORY_PROBE_LIMIT } from './constants'

type GalleryMessage =
  | HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
  | HistoryMessageItem<MessageCallRecordCompleted>

const isCallRecordCompletedMessage = (
  message: HistoryMessageItem,
): message is HistoryMessageItem<MessageCallRecordCompleted> => {
  return (message.item as MessageCallReceived)?.event === 'call_record_completed'
}

const isGalleryHistoryMessage = (message: HistoryMessageItem): message is GalleryMessage => {
  return isTextMessage(message) || isCallRecordCompletedMessage(message)
}

type RequestOlderHistoryParams = {
  pServedValue: string
  messagesLimit: number
  cursorEventId?: number
}

export const useChatGalleryHistory = (pServed: Ref<string>) => {
  const { requestChatHistory } = useIM()
  const { findRoomMessages } = chatModel.useChatMessageStore()
  const { filesMarkersByPServedMap } = storeToRefs(useChatFilesMarkersStore())

  const galleryMessages = ref<GalleryMessage[]>([])
  const isLoading = ref(false)
  const hasMoreHistory = ref(true)
  /** Cursor for the next older-history request; advanced even when batch adds no new messages. */
  const olderHistoryCursorEventId = ref<number | undefined>(undefined)

  const roomMessages = computed(() => findRoomMessages(pServed.value))

  const allMessages = computed(() => {
    const uniqueMessages = new Map<number, GalleryMessage>()

    ;[...roomMessages.value, ...galleryMessages.value].forEach((message) => {
      const eventId = message.eventId
      if (eventId && !uniqueMessages.has(eventId)) {
        uniqueMessages.set(eventId, message as GalleryMessage)
      }
    })

    return [...uniqueMessages.values()]
  })

  const markerFilesCount = computed(() => {
    if (!pServed.value) {
      return 0
    }

    return filesMarkersByPServedMap.value.get(pServed.value)?.count ?? 0
  })

  const loadedFilesCount = computed(() => {
    return allMessages.value
      .filter(isTextMessage)
      .reduce((count, message) => count + (message.item.files?.length ?? 0), 0)
  })

  const canLoadMoreHistory = computed(() => hasMoreHistory.value && !isLoading.value)

  const hasPendingFiles = computed(() => {
    return markerFilesCount.value > loadedFilesCount.value
  })

  const getOldestKnownEventId = () => {
    if (olderHistoryCursorEventId.value !== undefined) {
      return olderHistoryCursorEventId.value
    }

    if (galleryMessages.value.length) {
      return galleryMessages.value
        .slice()
        .sort(sort.chatEvents.byTimestamp)
        .at(-1)?.eventId
    }

    return roomMessages.value.at(0)?.eventId
  }

  const reset = () => {
    galleryMessages.value = []
    isLoading.value = false
    hasMoreHistory.value = true
    olderHistoryCursorEventId.value = undefined
  }

  const appendGalleryEvents = (events: GalleryMessage[]) => {
    if (!events.length) {
      return 0
    }

    const existingIds = new Set([
      ...roomMessages.value.map(({ eventId }) => eventId),
      ...galleryMessages.value.map(({ eventId }) => eventId),
    ])

    const newEvents = events.filter(({ eventId }) => eventId && !existingIds.has(eventId))
    if (newEvents.length) {
      galleryMessages.value = [...galleryMessages.value, ...newEvents]
    }

    return newEvents.length
  }

  const requestOlderHistory = async (params: RequestOlderHistoryParams) => {
    const requestPayload = {
      pServed: params.pServedValue,
      isConference: pServedIsRoom(params.pServedValue),
      messagesLimit: params.messagesLimit,
      type: 'older',
    } as const

    if (params.cursorEventId === undefined) {
      return requestChatHistory(requestPayload)
    }

    return requestChatHistory({
      ...requestPayload,
      cursorEventId: params.cursorEventId,
    })
  }

  const syncHasMoreHistory = async () => {
    const pServedValue = pServed.value
    const cursorEventId = getOldestKnownEventId()

    if (!pServedValue || cursorEventId === undefined) {
      hasMoreHistory.value = false
      return
    }

    const { events } = await requestOlderHistory({
      pServedValue,
      cursorEventId,
      messagesLimit: GALLERY_HISTORY_PROBE_LIMIT,
    })

    if (pServedValue !== pServed.value) {
      return
    }

    hasMoreHistory.value = (events?.length ?? 0) > 0
  }

  const fetchOlderBatch = async () => {
    const pServedValue = pServed.value
    if (!pServedValue || !hasMoreHistory.value) {
      return
    }

    const cursorEventId = getOldestKnownEventId()
    const { events } = cursorEventId !== undefined
      ? await requestOlderHistory({
        pServedValue,
        cursorEventId,
        messagesLimit: GALLERY_HISTORY_BATCH_LIMIT,
      })
      : await requestOlderHistory({
        pServedValue,
        messagesLimit: GALLERY_HISTORY_BATCH_LIMIT,
      })

    if (pServedValue !== pServed.value) {
      return
    }

    if (!events?.length) {
      hasMoreHistory.value = false
      return
    }

    const eventsSorted = events.slice().sort(sort.chatEvents.byTimestamp)
    const nextCursor = eventsSorted.at(-1)?.eventId
    const eventsToAppend = events.filter(isGalleryHistoryMessage)
    const addedCount = appendGalleryEvents(eventsToAppend)

    if (nextCursor !== undefined) {
      olderHistoryCursorEventId.value = nextCursor
    }

    if (events.length < GALLERY_HISTORY_BATCH_LIMIT) {
      hasMoreHistory.value = false
      return
    }

    if (!nextCursor || nextCursor === cursorEventId) {
      hasMoreHistory.value = false
      return
    }

    // Full batch of already-known / non-text events: cursor advanced, but stop this pass
    // so a single click/IO tick cannot spin forever on the same viewport.
    if (addedCount === 0) {
      return
    }
  }

  const loadOlder = async () => {
    if (isLoading.value || !hasMoreHistory.value) {
      return
    }

    isLoading.value = true
    try {
      await fetchOlderBatch()
    } catch {
      console.error('Error loading chat gallery history')
    } finally {
      isLoading.value = false
    }
  }

  const loadInitial = async () => {
    if (roomMessages.value.length > 0) {
      await syncHasMoreHistory()
      return
    }

    await loadOlder()
  }

  watch(pServed, () => {
    reset()
    void loadInitial()
  }, { immediate: true })

  return {
    allMessages,
    canLoadMoreHistory,
    galleryMessages,
    hasMoreHistory,
    hasPendingFiles,
    isLoading,
    loadedFilesCount,
    loadInitial,
    loadOlder,
    markerFilesCount,
    reset,
  }
}
